import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import {
  GripVertical,
  GripHorizontal,
  ArrowLeftRight,
  Search,
  PanelLeft,
  PanelRight,
  PanelBottom,
  Layout,
  HelpCircle,
  Pencil,
  AlertCircle,
  X,
  Database,
  MessageSquare,
  Sigma,
  Clock,
  BarChart2,
  Map as MapIcon,
  History,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { isPlainLeftClick, workspaceNewChatPath } from '../lib/navigation';
import { getDefaultPalette } from '../utils/colorPalettes';
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from 'react-resizable-panels';
import { useLayout } from '../context/LayoutContext';

import PreviewTable from '../components/workspace/PreviewTable';
import ConnectModal from '../components/workspace/ConnectModal';
import BillingProjectModal from '../components/workspace/BillingProjectModal';
import WorkspaceSummaryModal from '../components/workspace/WorkspaceSummaryModal';
import DatasetPreview from '../components/workspace/Data/DatasetPreview';
import ChatPanel from '../components/workspace/Chat/ChatPanel';
import MapLayerList from '../components/workspace/Map/MapLayerList';
import MapLayerBuilderModal from '../components/workspace/Map/MapLayerBuilderModal';
import DataPanelTabs from '../components/workspace/Data/DataPanelTabs';
import MapPanel from '../components/workspace/Map/MapPanel';
import ConfirmModal from '../components/common/ConfirmModal';
import FunctionPreview from '../components/workspace/Data/FunctionPreview';
import FunctionDetailModal from '../components/functions/FunctionDetailModal';
import FunctionLibraryModal from '../components/workspace/FunctionLibraryModal';
import AddItemModal from '../components/workspace/AddItemModal';
import GuidedTour from '../components/workspace/GuidedTour';

import { buildInitialMessages } from '../data/mockData';
import { workspaceApi } from '../api/workspaceApi';
import { chatApi } from '../api/chatApi';
import { mapApi } from '../api/mapApi';
import { dashboardApi } from '../api/dashboardApi';
import {
  getGeoJSONFitBounds,
  h3ToGeoJSON,
  itemsToGeoJSON,
  MAP_MAX_FEATURES_PER_LAYER_LS_KEY,
  MAP_MAX_FEATURES_PER_LAYER_DEFAULT,
  parseMapMaxFeaturesPerLayer
} from '../utils/mapUtils';
import { buildQueryValue, MAP_NO_METRIC } from '../utils/mapLayerMetricUtils';
import {
  extractPersistableLayerConfig,
  mergeSavedLayerConfig,
} from '../utils/mapLayerConfig';
import { buildOptimisticUploadDataset } from '../utils/datasetDisplayUtils';
import {
  computeStatsFromGeoJsonProperty,
  isHighCardinalityCategorical,
  layerHasColorableProperty
} from '../utils/mapUtils';
import { datasetApi } from '../api/datasetApi';
import { bigqueryApi } from '../api/bigqueryApi';
import { showErrorToast, showInfoToast, showSuccessToast } from '../lib/toast';
import {
  MAX_DATASETS_PER_WORKSPACE,
  MAX_FUNCTIONS_PER_WORKSPACE
} from '../constants/limits';
import { MAP_DATA_LOAD_ERROR_HINT } from '../constants/mapMessages';
import { useAuth } from '../context/AuthContext';
import { FEATURE_INTEGRATION_UDF } from '../constants/capabilities';
import NotFoundPage from './NotFoundPage';

const formatRelativeShort = (iso) => {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const diff = Math.max(0, Date.now() - ts);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))}m`;
  if (diff < day) return `${Math.floor(diff / hour)}h`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w`;
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))}mo`;
  return `${Math.floor(diff / (365 * day))}y`;
};

const isNoDatasetsChatError = (content) => {
  if (!content || typeof content !== 'string') return false;
  const lower = content.toLowerCase();
  return lower.includes('no datasets selected') || lower.includes('select at least one dataset');
};

const PANEL_TOGGLES = [
  { key: 'chat', label: 'Chat', icon: PanelLeft },
  { key: 'data', label: 'Data', icon: PanelBottom },
  { key: 'map', label: 'Map', icon: PanelRight },
];

const HISTORY_TAB_ITEMS = [
  { id: 'chats', label: 'chat', icon: MessageSquare },
  { id: 'plots', label: 'plot', icon: BarChart2 },
  { id: 'maps', label: 'map', icon: MapIcon },
];

const getHistoryTabFromEvent = (event) =>
  event.target.closest('[data-history-tab]')?.getAttribute('data-history-tab') || 'overview';

const HEADER_EYEBROW =
  'text-[10px] font-semibold uppercase tracking-wider leading-none text-gray-500 dark:text-gray-400';
const HEADER_TITLE =
  'text-sm font-semibold tracking-tight leading-none text-gray-900 dark:text-white';
const HEADER_CHIP =
  'rounded-xl border border-gray-200 bg-white px-2 py-1.5 dark:border-gray-800 dark:bg-gray-900';
const HEADER_ACTION =
  'text-[10px] font-semibold uppercase tracking-wider leading-none';
const HEADER_BADGE =
  'text-[9px] font-semibold tabular-nums leading-none';
const HEADER_ICON_SIZE = 14;

const HeaderUpdater = ({
  workspace,
  workspaceId,
  bqStatus,
  showPanels,
  togglePanel,
  onStartTour,
  workspaceStats,
  workspaceStatsLoading,
  onOpenSummary,
}) => {
  const { setHeaderContent } = useLayout();
  const { capabilities } = useAuth();
  const udfEnabled = capabilities?.features?.[FEATURE_INTEGRATION_UDF] !== false;
  const navigate = useNavigate();
  const [systemLive, setSystemLive] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [isWorkspaceSidebarOpen, setIsWorkspaceSidebarOpen] = useState(false);
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState('');
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        // Use lightweight liveness endpoint for frequent checks
        const health = await dashboardApi.getHealth();
        setSystemLive(health.status === 'ok');
      } catch {
        setSystemLive(false);
      }
    };
    checkHealth();
    // Increased interval to 60s to reduce load - health checks are expensive
    const interval = setInterval(checkHealth, 60000); // Check every 60s
    return () => clearInterval(interval);
  }, []);

  // Load workspaces for the sidebar only when it opens, ordered by creation date (newest first).
  useEffect(() => {
    if (!isWorkspaceSidebarOpen) return;
    setIsLoadingWorkspaces(true);
    workspaceApi
      .getWorkspaces()
      .then((data) => {
        const sorted = [...data].sort(
          (a, b) =>
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
        setWorkspaces(sorted);
      })
      .catch((err) => console.error('Failed to load workspaces:', err))
      .finally(() => setIsLoadingWorkspaces(false));
  }, [isWorkspaceSidebarOpen]);

  const handleToggleSidebar = useCallback(() => {
    setIsWorkspaceSidebarOpen((prev) => !prev);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setIsWorkspaceSidebarOpen(false);
    setWorkspaceSearchQuery('');
  }, []);

  const filteredWorkspaces = useMemo(() => {
    const query = workspaceSearchQuery.trim().toLowerCase();
    if (!query) return workspaces;
    return workspaces.filter((ws) => {
      const name = (ws.name || '').toLowerCase();
      const description = (ws.description || '').toLowerCase();
      return name.includes(query) || description.includes(query);
    });
  }, [workspaces, workspaceSearchQuery]);

  // Close sidebar on Escape for better keyboard usability
  useEffect(() => {
    if (!isWorkspaceSidebarOpen) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCloseSidebar();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isWorkspaceSidebarOpen, handleCloseSidebar]);

  useEffect(() => {
    if (workspace && workspaceId) {
      setHeaderContent(
        <div className="w-full flex items-center justify-between h-full gap-2 lg:gap-4 min-w-0">
          {/* Left section: Workspace + chat status */}
          <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
            <div className="relative shrink-0 mr-1">
              <button
                type="button"
                data-tour="workspace-selector"
                onClick={handleToggleSidebar}
                className={cn(
                  "flex items-center gap-2 group transition-all border shrink-0",
                  HEADER_CHIP,
                  isWorkspaceSidebarOpen
                    ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                    : "hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10"
                )}
                aria-label="Open workspace sidebar"
                aria-expanded={isWorkspaceSidebarOpen}
              >
                <ArrowLeftRight
                  size={HEADER_ICON_SIZE}
                  className={cn(
                    "shrink-0 transition-colors",
                    isWorkspaceSidebarOpen
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-gray-500 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                  )}
                />
                <div className="flex flex-col shrink-0 min-w-0 text-left">
                  <span className={cn(HEADER_EYEBROW, 'mb-0.5')}>Workspace</span>
                  <span className={cn(HEADER_TITLE, 'truncate max-w-[120px] sm:max-w-[160px] lg:max-w-[220px]')}>{workspace.name}</span>
                </div>
              </button>

              {isWorkspaceSidebarOpen && createPortal(
                <div className="fixed inset-0 z-[100] flex">
                  <button
                    type="button"
                    className="flex-1 bg-gray-900/45 backdrop-blur-[1px]"
                    onClick={handleCloseSidebar}
                    aria-label="Close workspace sidebar"
                  />
                  <aside className="w-96 max-w-[92vw] h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200">
                    <div className="h-full flex flex-col">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Workspaces</h3>
                            <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">
                              {filteredWorkspaces.length} of {workspaces.length}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleCloseSidebar}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            aria-label="Close workspace sidebar"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="relative">
                          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                          <input
                            type="text"
                            value={workspaceSearchQuery}
                            onChange={(e) => setWorkspaceSearchQuery(e.target.value)}
                            placeholder="Search workspaces"
                            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                          />
                        </div>
                      </div>
                      <div className="p-2 overflow-y-auto">
                        <div className="space-y-0.5">
                          {isLoadingWorkspaces && workspaces.length === 0 ? (
                            <div className="px-3 py-6 text-center">
                              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Loading workspaces…</p>
                            </div>
                          ) : filteredWorkspaces.length === 0 ? (
                            <div className="px-3 py-6 text-center">
                              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">No workspaces found</p>
                            </div>
                          ) : filteredWorkspaces.map((ws) => {
                            const datasetCount = ws.sources?.length || 0;
                            const functionCount = ws.udfs?.length || 0;
                            const chatCount = ws.chatsCount || 0;
                            const createdLabel = formatRelativeShort(ws.created_at);
                            const createdTitle = ws.created_at
                              ? `Created ${new Date(ws.created_at).toLocaleString()}`
                              : null;
                            return (
                              <Link
                                key={ws.id}
                                to={`/workspace/${ws.id}`}
                                onClick={(e) => {
                                  if (!isPlainLeftClick(e)) return;
                                  handleCloseSidebar();
                                }}
                                className={cn(
                                  "w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all group",
                                  ws.id === workspaceId
                                    ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent"
                                )}
                              >
                                <Layout
                                  size={16}
                                  className={cn(
                                    "shrink-0",
                                    ws.id === workspaceId
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : "text-gray-400 dark:text-gray-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                                  )}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold tracking-tight text-sm leading-tight line-clamp-2 break-words">{ws.name}</div>
                                  {ws.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 break-words">
                                      {ws.description}
                                    </div>
                                  )}
                                  <div className="mt-1 flex items-center gap-2.5 text-[10px] font-semibold text-gray-500 dark:text-gray-400 tabular-nums">
                                    <span className="inline-flex items-center gap-1" title={`${datasetCount} dataset${datasetCount === 1 ? '' : 's'}`}>
                                      <Database size={10} strokeWidth={2.5} />
                                      {datasetCount}
                                    </span>
                                    {udfEnabled && (
                                      <span className="inline-flex items-center gap-1" title={`${functionCount} function${functionCount === 1 ? '' : 's'}`}>
                                        <Sigma size={10} strokeWidth={2.5} />
                                        {functionCount}
                                      </span>
                                    )}
                                    <span className="inline-flex items-center gap-1" title={`${chatCount} chat${chatCount === 1 ? '' : 's'}`}>
                                      <MessageSquare size={10} strokeWidth={2.5} />
                                      {chatCount}
                                    </span>
                                    {createdLabel && (
                                      <span className="inline-flex items-center gap-1" title={createdTitle}>
                                        <Clock size={10} strokeWidth={2.5} />
                                        {createdLabel}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {ws.id === workspaceId && (
                                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </aside>
                </div>,
                document.body
              )}
            </div>

            <button
              type="button"
              onClick={() => onOpenSummary?.('overview')}
              className={cn(
                'sm:hidden shrink-0 transition-colors hover:border-emerald-300 dark:hover:border-emerald-700',
                HEADER_CHIP
              )}
              title="Browse past chats, plots, and maps in this workspace"
              aria-label="Workspace history"
            >
              <History size={HEADER_ICON_SIZE} strokeWidth={2} className="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={(e) => onOpenSummary?.(getHistoryTabFromEvent(e))}
              className={cn(
                'group/history hidden sm:flex items-center gap-1.5 lg:gap-2 shrink-0 transition-colors hover:border-emerald-300 dark:hover:border-emerald-700',
                HEADER_CHIP
              )}
              title="Browse past chats, plots, and maps in this workspace"
              aria-label="Workspace history"
            >
              <span className="inline-flex items-center gap-1.5 rounded-lg px-1 py-0.5 -ml-1 transition-colors group-hover/history:bg-emerald-50/70 dark:group-hover/history:bg-emerald-950/20">
                <History size={HEADER_ICON_SIZE} strokeWidth={2} className="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                <span className={cn(HEADER_EYEBROW, 'whitespace-nowrap hidden lg:inline')}>
                  Workspace history
                </span>
              </span>
              <div className="flex items-center gap-0.5 border-l border-gray-200 pl-1.5 dark:border-gray-700">
                {HISTORY_TAB_ITEMS.map(item => {
                  const ItemIcon = item.icon;
                  const count = workspaceStats?.[item.id] ?? 0;
                  const countLabel = `${count} ${item.label}${count === 1 ? '' : 's'}`;
                  return (
                    <span
                      key={item.id}
                      data-history-tab={item.id}
                      title={workspaceStatsLoading ? 'Loading…' : `${countLabel} — open workspace history`}
                      className="group/item relative inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 transition-all hover:bg-emerald-100 hover:text-emerald-700 hover:ring-1 hover:ring-emerald-300/70 dark:text-gray-400 dark:hover:bg-emerald-900/60 dark:hover:text-emerald-300 dark:hover:ring-emerald-700/70"
                    >
                      <ItemIcon size={HEADER_ICON_SIZE} strokeWidth={2} aria-hidden="true" />
                      {!workspaceStatsLoading && (
                        <span className={cn(
                          'absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full border border-emerald-200/80 bg-emerald-50 px-0.5 text-emerald-800 transition-colors group-hover/item:border-emerald-300 group-hover/item:bg-emerald-200 group-hover/item:text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/50 dark:text-emerald-300 dark:group-hover/item:border-emerald-600 dark:group-hover/item:bg-emerald-900/80 dark:group-hover/item:text-emerald-200',
                          HEADER_BADGE
                        )}>
                          {count}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </button>

            {bqStatus?.reauth_required && workspace?.sources?.some(s => s.type === 'bigquery') && (
              <div
                className="flex items-center gap-1 mt-0.5 px-2 py-1 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg animate-in fade-in zoom-in duration-300 w-fit shrink-0"
                title="BigQuery connection expired. Please reconnect in 'Data Sources'."
              >
                <AlertCircle size={HEADER_ICON_SIZE} className="text-red-600 dark:text-red-400 shrink-0" />
                <span className={cn(HEADER_EYEBROW, 'text-red-600 dark:text-red-400 hidden md:inline')}>BQ Re-auth</span>
              </div>
            )}
          </div>

          {/* Right section: View/Analysis Panel buttons */}
          <div data-tour="panel-toggles" className="flex items-center gap-1 shrink-0 bg-gray-50/50 dark:bg-gray-900/50 p-1 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm">
            {PANEL_TOGGLES.map(({ key, label, icon: Icon }) => {
              const isActive = showPanels[key];
              if (isActive) {
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => togglePanel(key)}
                    className="flex items-center gap-0.5 pl-2 lg:pl-3 pr-1.5 py-1.5 rounded-lg text-black dark:text-white bg-white dark:bg-gray-800 shadow-sm ring-1 ring-black/5 dark:ring-white/5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/80"
                    aria-label={`Hide ${label.toLowerCase()} panel`}
                  >
                    <span className={cn('flex items-center gap-1.5 lg:gap-2', HEADER_ACTION)}>
                      <Icon size={HEADER_ICON_SIZE} strokeWidth={2} />
                      <span className="hidden lg:inline">{label}</span>
                    </span>
                    <X size={12} strokeWidth={2.5} className="text-gray-400 shrink-0" aria-hidden="true" />
                  </button>
                );
              }
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => togglePanel(key)}
                  className={cn(
                    'px-2 lg:px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 lg:gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
                    HEADER_ACTION
                  )}
                  aria-label={`Show ${label.toLowerCase()} panel`}
                >
                  <Icon size={HEADER_ICON_SIZE} strokeWidth={2} />
                  <span className="hidden lg:inline">{label}</span>
                </button>
              );
            })}
            <button
              onClick={onStartTour}
              className="hidden md:flex p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900"
              title="Take a guided tour"
              aria-label="Take a guided tour"
            >
              <HelpCircle size={HEADER_ICON_SIZE} />
            </button>
          </div>
        </div>
      );
    }
    return () => setHeaderContent(null);
  }, [workspace, workspaceId, bqStatus, showPanels, setHeaderContent, togglePanel, systemLive, isWorkspaceSidebarOpen, workspaces, filteredWorkspaces, workspaceSearchQuery, navigate, onStartTour, handleToggleSidebar, handleCloseSidebar, udfEnabled, isLoadingWorkspaces, workspaceStats, workspaceStatsLoading, onOpenSummary]);

  return null;
};

const WorkspacePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspace, setWorkspace] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [chatTitle, setChatTitle] = useState('Untitled Chat');
  const [messages, setMessages] = useState([]);
  const [previewDataset, setPreviewDataset] = useState(null);
  const [previewFunction, setPreviewFunction] = useState(null);
  const [previewMetadataVersion, setPreviewMetadataVersion] = useState(0);
  const [isFullScreenPreviewOpen, setIsFullScreenPreviewOpen] = useState(false);
  const [isFunctionDetailOpen, setIsFunctionDetailOpen] = useState(false);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isFunctionLibraryModalOpen, setIsFunctionLibraryModalOpen] = useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [addItemModalType, setAddItemModalType] = useState('dataset'); // 'dataset' | 'function'
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [shouldResumeImport, setShouldResumeImport] = useState(false);
  const [connectModalInitialStep, setConnectModalInitialStep] = useState(null);
  const [bqStatus, setBqStatus] = useState({ connected: false });
  const workspaceNeedsBqReauth = Boolean(
    workspace?.sources?.some((s) => s.type === 'bigquery') && bqStatus?.reauth_required
  );
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [summaryModalTab, setSummaryModalTab] = useState('overview');
  const [workspaceHistory, setWorkspaceHistory] = useState({ chats: [], artefacts: [] });
  const [isWorkspaceHistoryLoading, setIsWorkspaceHistoryLoading] = useState(false);
  const [workspaceStats, setWorkspaceStats] = useState({ chats: 0, plots: 0, maps: 0 });
  const [isMapLayerBuilderModalOpen, setIsMapLayerBuilderModalOpen] = useState(false);
  const [isRemoveSourceModalOpen, setIsRemoveSourceModalOpen] = useState(false);
  const [removingSourceId, setRemovingSourceId] = useState(null);
  const [isRemoveFunctionModalOpen, setIsRemoveFunctionModalOpen] = useState(false);
  const [targetSourceToRemove, setTargetSourceToRemove] = useState(null);
  const [targetFunctionToRemove, setTargetFunctionToRemove] = useState(null);
  const [targetMessageId, setTargetMessageId] = useState(null);
  const [mapLayers, setMapLayers] = useState([]); // Removed MOCK_MAP_LAYERS
  const [showPanels, setShowPanels] = useState({
    chat: true,
    map: true,
    data: true
  });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const mapRef = useRef(null);
  const syncedUrlChatKey = useRef(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const CHAT_MODEL_TIER_LS_KEY = 'manteo_chat_model_tier';
  const [chatModelTier, setChatModelTier] = useState(() => {
    try {
      const v = localStorage.getItem(CHAT_MODEL_TIER_LS_KEY);
      if (v === 'deep' || v === 'fast') return v;
    } catch {
      /* ignore */
    }
    return 'deep';
  });
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_MODEL_TIER_LS_KEY, chatModelTier);
    } catch {
      /* ignore */
    }
  }, [chatModelTier]);

  const [maxFeaturesPerLayerMap, setMaxFeaturesPerLayerMap] = useState(() => {
    try {
      const v = localStorage.getItem(MAP_MAX_FEATURES_PER_LAYER_LS_KEY);
      return parseMapMaxFeaturesPerLayer(v);
    } catch {
      return MAP_MAX_FEATURES_PER_LAYER_DEFAULT;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(MAP_MAX_FEATURES_PER_LAYER_LS_KEY, String(maxFeaturesPerLayerMap));
    } catch {
      /* ignore */
    }
  }, [maxFeaturesPerLayerMap]);

  const [isInitializingChat, setIsInitializingChat] = useState(true);
  const abortControllerRef = useRef(null);
  const consumedViewArtefactRef = useRef(null);
  const [notFound, setNotFound] = useState(false);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const preTourPanelsRef = useRef(null);

  // Helper function to persist chatId to URL
  const persistChatId = useCallback((chatIdToPersist) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (chatIdToPersist) {
      newSearchParams.set('chat', chatIdToPersist);
    } else {
      newSearchParams.delete('chat');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Reset chat state when switching workspaces; URL sync effect loads the target chat
  useEffect(() => {
    syncedUrlChatKey.current = null;
    setChatId(null);
    setChatTitle('Untitled Chat');
    setMessages(buildInitialMessages(null));
    setIsInitializingChat(true);
  }, [id]);

  const urlChatId = searchParams.get('chat');

  const newChatTo = useMemo(
    () => (id ? workspaceNewChatPath(id, searchParams) : null),
    [id, searchParams]
  );

  const handleSelectChat = useCallback((chatIdToSelect, messages, title) => {
    setChatId(chatIdToSelect);
    setChatTitle(title || 'Untitled Chat');
    setIsInitializingChat(false);
    persistChatId(chatIdToSelect); // Persist to URL
    if (!messages || messages.length === 0) {
      setMessages(buildInitialMessages(workspace));
    } else {
      setMessages(messages.map(m => ({
        id: m.id,
        role: m.user_id === '00000000-0000-0000-0000-000000000000' ? 'assistant' : 'user',
        content: m.content,
        // Plot data
        sql: m.extra?.sql,
        chartData: m.extra?.rows,
        spec: m.extra?.visualizationSpec,
        tableOnly: m.extra?.presentation === 'table' || !!m.extra?.table_only,
        visualization: m.extra?.visualizationSpec?.title,
        rowCount: m.extra?.rows ? m.extra.rows.length : (m.extra?.rowCount || 0),
        insight: m.extra?.insight || null,
        // Map data
        isMapResult: m.type === 'map' || !!m.extra?.layer_id || !!m.extra?.map_layer_id,
        layer_id: m.extra?.layer_id || m.extra?.map_layer_id || m.extra?.map_query?.layer_id,
        name: m.extra?.name || m.extra?.map_query?.name,
        // Base metadata
        type: m.type, // Preserve type to identify artifact messages
        extra: m.extra, // Preserve extra to check for artifact data
        isLoading: false
      })));
    }
  }, [persistChatId, workspace]);

  const handleGenerateTitle = useCallback(async (targetChatId) => {
    const cid = targetChatId || chatId;
    if (!cid) return;
    try {
      const data = await chatApi.generateTitle(cid);
      setChatTitle(data.title);
    } catch (err) {
      console.error('Failed to generate title:', err);
    }
  }, [chatId]);

  const loadWorkspaceHistory = useCallback(async () => {
    if (!id) return;

    setIsWorkspaceHistoryLoading(true);
    try {
      const [chats, artefacts] = await Promise.all([
        chatApi.getChats(id),
        workspaceApi.getWorkspaceArtefacts(id),
      ]);
      setWorkspaceHistory({ chats, artefacts });
      setWorkspaceStats({
        chats: chats.length,
        plots: artefacts.filter(art => art.type === 'chart').length,
        maps: artefacts.filter(art => art.type === 'map').length,
      });
    } catch (err) {
      console.error('Failed to load workspace history:', err);
    } finally {
      setIsWorkspaceHistoryLoading(false);
    }
  }, [id]);

  const handleSelectArtefact = useCallback(async (artefact) => {
    try {
      if (artefact.type === 'map') {
        const stableId = `art-${artefact.id}`;
        // Calling handleMapQuery ensures data is fetched and layer is correctly added/updated
        // Pass artefact.id as layerId (UUID) to load from database, and stableId in config for frontend ID
        await handleMapQuery(artefact.id, artefact.title || 'Saved Map Layer', { ...artefact.config, stableId });
        setShowPanels(prev => ({ ...prev, map: true }));
        setIsSummaryModalOpen(false);
        loadWorkspaceHistory();
        return;
      }

      // 1. Fetch chat messages
      const chatMessages = await chatApi.getMessages(artefact.chat_id);

      // 2. Fetch chat info to get the title
      const chats = await chatApi.getChats(id);
      const currentChat = chats.find(c => c.id === artefact.chat_id);

      // 3. Update current chat and messages
      handleSelectChat(artefact.chat_id, chatMessages, currentChat?.title);

      // 4. Ensure chat panel is visible
      setShowPanels(prev => ({ ...prev, chat: true }));

      // 5. Set the target message ID for scrolling
      setTargetMessageId(artefact.id);

      // 6. Close summary modal
      setIsSummaryModalOpen(false);
      loadWorkspaceHistory();
    } catch (err) {
      console.error('Failed to select artefact:', err);
    }
  }, [id, handleSelectChat, loadWorkspaceHistory]);

  const handleOpenSummary = useCallback((tab = 'overview') => {
    setSummaryModalTab(tab);
    setIsSummaryModalOpen(true);
    if (!isChatLoading) {
      loadWorkspaceHistory();
    }
  }, [isChatLoading, loadWorkspaceHistory]);

  const handleUpdateWorkspaceFromModal = useCallback(async (workspaceId, updates) => {
    const updatedWs = await workspaceApi.updateWorkspace(workspaceId, updates);
    setWorkspace(updatedWs);
    showSuccessToast('Workspace updated.');
    return updatedWs;
  }, []);

  const loadWorkspace = useCallback(async (silent = false) => {
    try {
      setNotFound(false);
      const data = await workspaceApi.getWorkspaceById(id);
      setWorkspace(data);
    } catch (err) {
      const status = err.response?.status;
      if (status >= 400 && status < 500) {
        setNotFound(true);
      } else if (status >= 500) {
        setNotFound(true);
      } else {
        console.error('Failed to load workspace:', err);
      }
      return;
    }

    try {
      const bq = await bigqueryApi.getStatus();
      setBqStatus(bq);
    } catch (err) {
      // BigQuery status should not block opening a workspace.
      console.warn('Failed to load BigQuery status:', err);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    loadWorkspace();
  }, [id, loadWorkspace]);

  useEffect(() => {
    if (!id) return;
    setWorkspaceStats({ chats: 0, plots: 0, maps: 0 });
    setIsWorkspaceHistoryLoading(true);
  }, [id]);

  useEffect(() => {
    if (!id || isChatLoading) return;
    loadWorkspaceHistory();
  }, [id, isChatLoading, loadWorkspaceHistory]);

  useEffect(() => {
    consumedViewArtefactRef.current = null;
  }, [id]);

  useEffect(() => {
    const viewArtefact = location.state?.viewArtefact;
    if (!viewArtefact || !workspace) return;
    if (consumedViewArtefactRef.current === viewArtefact.id) return;

    consumedViewArtefactRef.current = viewArtefact.id;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
    handleSelectArtefact(viewArtefact);
  }, [location.state, workspace, navigate, location.pathname, location.search, handleSelectArtefact]);

  // Keep chat state in sync with ?chat= URL (supports Link navigation and new-tab opens)
  useEffect(() => {
    if (!id) return;

    const syncKey = `${id}:${urlChatId ?? ''}`;
    if (syncedUrlChatKey.current === syncKey) return;
    if (urlChatId && urlChatId === chatId) {
      syncedUrlChatKey.current = syncKey;
      setIsInitializingChat(false);
      return;
    }
    if (!urlChatId && !chatId) {
      syncedUrlChatKey.current = syncKey;
      setIsInitializingChat(false);
      setMessages((prev) => (prev.length === 0 ? buildInitialMessages(workspace) : prev));
      return;
    }

    let cancelled = false;

    const syncFromUrl = async () => {
      if (!urlChatId) {
        setChatId(null);
        setChatTitle('Untitled Chat');
        setMessages(buildInitialMessages(workspace));
        setIsInitializingChat(false);
        syncedUrlChatKey.current = syncKey;
        return;
      }

      setIsInitializingChat(true);
      try {
        const chats = await chatApi.getChats(id);
        const chatToLoad = chats.find((c) => c.id === urlChatId);
        if (cancelled) return;

        if (chatToLoad) {
          const chatMessages = await chatApi.getMessages(chatToLoad.id);
          if (cancelled) return;
          handleSelectChat(chatToLoad.id, chatMessages, chatToLoad.title);
          setIsSummaryModalOpen(false);
        } else {
          persistChatId(null);
          setChatId(null);
          setChatTitle('Untitled Chat');
          setMessages(buildInitialMessages(workspace));
        }
      } catch (err) {
        console.error('Failed to sync chat from URL:', err);
        if (!cancelled) {
          setMessages(buildInitialMessages(workspace));
        }
      } finally {
        if (!cancelled) {
          setIsInitializingChat(false);
          syncedUrlChatKey.current = syncKey;
        }
      }
    };

    syncFromUrl();
    return () => {
      cancelled = true;
    };
  }, [id, urlChatId, chatId, handleSelectChat, persistChatId, workspace]);

  useEffect(() => {
    if (!workspace || urlChatId || chatId) return;
    setMessages((prev) => {
      if (prev.some((m) => m.role === 'user')) return prev;
      return buildInitialMessages(workspace);
    });
  }, [workspace, urlChatId, chatId]);

  // Polling for workspace dataset updates
  useEffect(() => {
    if (!workspace?.sources) return;

    const hasPending = workspace.sources.some(s => s.status === 'loading' || s.status === 'generating');
    if (!hasPending) return;

    const interval = setInterval(() => {
      loadWorkspace(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [workspace?.sources, loadWorkspace]);

  const promptAddDataset = useCallback(() => {
    setShowPanels((prev) => (prev.data ? prev : { ...prev, data: true }));
  }, []);

  const handleAddDataset = useCallback(() => {
    setAddItemModalType('dataset');
    setIsAddItemModalOpen(true);
  }, []);

  // OAuth return: refresh BigQuery status so the re-auth blocker can clear
  useEffect(() => {
    if (!searchParams.get('bigquery-connected')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('bigquery-connected');
    setSearchParams(next, { replace: true });
    bigqueryApi.getStatus().then(setBqStatus).catch(() => {});
  }, [searchParams, setSearchParams]);

  // If the user re-authenticated in another tab, clear the blocker on focus
  useEffect(() => {
    if (!workspaceNeedsBqReauth) return;
    const onFocus = () => {
      bigqueryApi.getStatus().then(setBqStatus).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [workspaceNeedsBqReauth]);


  useEffect(() => {
    if (workspace) {
      document.title = `manteo.ai | ${workspace.name}`;
    } else {
      document.title = 'manteo.ai | Workspace';
    }
  }, [workspace]);

  // Auto-start the guided tour on first visit
  useEffect(() => {
    if (workspace && !localStorage.getItem('manteo_tour_completed')) {
      // Small delay to let the workspace panels render
      const timer = setTimeout(() => {
        handleStartTour();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [workspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReorderLayers = useCallback((newOrder) => {
    setMapLayers(newOrder);
  }, []);

  const persistMapLayerArtefact = useCallback(async (artefactId, updates = {}) => {
    if (!artefactId) return;
    const payload = {};
    if (updates.title != null) payload.title = updates.title;
    if (updates.spec != null) payload.spec = updates.spec;
    if (updates.config != null) {
      payload.config = extractPersistableLayerConfig(updates.config);
    }
    if (Object.keys(payload).length === 0) return;

    try {
      await workspaceApi.updateArtefact(String(artefactId), payload, {
        suppressGlobalErrorToast: true,
      });
    } catch (err) {
      console.warn('Failed to persist layer settings:', err);
    }
  }, []);

  const persistLayerStyle = useCallback((layerId) => {
    const layer = mapLayers.find((l) => l.id === layerId);
    if (!layer?.layer_id) return;
    persistMapLayerArtefact(String(layer.layer_id), { config: layer.config });
  }, [mapLayers, persistMapLayerArtefact]);

  const toggleLayerVisibility = (layerId) => {
    setMapLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, visible: !l.visible } : l
    ));
  };

  const updateLayerColorProperty = (layerId, propertyName) => {
    setMapLayers((prev) =>
      prev.map((l) => {
        if (l.id !== layerId) return l;

        if (propertyName === MAP_NO_METRIC) {
          return {
            ...l,
            config: { ...l.config, uniformColor: true, colorProperty: null },
            stats: { min: 0, max: 1, is_categorical: false },
            valueFilter: null
          };
        }

        if (!layerHasColorableProperty(l, propertyName)) {
          return l;
        }

        const stats = computeStatsFromGeoJsonProperty(l.data, propertyName);
        const highCardinality = isHighCardinalityCategorical(
          { ...l, stats, config: { ...l.config, colorProperty: propertyName } },
          propertyName
        );

        const { classificationMethod, numClasses, classBreaks, ...restConfig } = l.config || {};
        return {
          ...l,
          config: {
            ...restConfig,
            colorProperty: propertyName,
            palette: getDefaultPalette(),
            inverted: false,
            uniformColor: highCardinality,
            highCardinalityCategorical: highCardinality
          },
          stats,
          valueFilter: null
        };
      })
    );
  };

  const handleUpdateLayerMetric = async (layerId, valueColumn, aggregation) => {
    const layer = mapLayers.find((l) => l.id === layerId);
    if (!layer) return;

    const value = buildQueryValue(valueColumn, aggregation);
    let queryPayload;

    if (layer.layer_id) {
      queryPayload = { layer_id: layer.layer_id, value };
    } else if (layer.query?.dataset || layer.query?.source_sql) {
      queryPayload = { ...layer.query, value };
      delete queryPayload.layer_id;
    } else {
      return;
    }

    const useViridis = valueColumn !== MAP_NO_METRIC;

    const nextConfig = {
      palette: useViridis ? getDefaultPalette() : layer.config?.palette,
      inverted: false,
      colorProperty: 'value',
      uniformColor: valueColumn === MAP_NO_METRIC,
    };

    await handleMapQuery(queryPayload, layer.name, {
      stableId: layerId,
      ...nextConfig,
    });

    if (layer.layer_id) {
      const nextSpec = layer.query ? { ...layer.query, value } : null;
      if (nextSpec) {
        delete nextSpec.layer_id;
      }
      await persistMapLayerArtefact(layer.layer_id, {
        ...(nextSpec ? { spec: nextSpec } : {}),
        config: nextConfig,
      });
    }
  };

  const updateLayerTheme = (layerId, color, palette = null, inverted = false) => {
    setMapLayers(prev => prev.map(l => {
      if (l.id === layerId) {
        const newConfig = { ...l.config };

        // Always use palette mode
        if (palette !== null) {
          newConfig.palette = palette;
          newConfig.inverted = inverted;
        } else {
          // Default to viridis if no palette provided
          newConfig.palette = getDefaultPalette();
          newConfig.inverted = inverted;
        }

        // Remove any old color config
        delete newConfig.color;

        return {
          ...l,
          config: newConfig,
          palette: newConfig.palette,
        };
      }
      return l;
    }));
  };

  const updateLayerClassification = (layerId, classification) => {
    setMapLayers((prev) =>
      prev.map((l) => {
        if (l.id !== layerId) return l;
        const newConfig = { ...l.config, ...classification };
        if (classification.classificationMethod !== 'manual') {
          delete newConfig.classBreaks;
        }
        return { ...l, config: newConfig, valueFilter: null };
      })
    );
  };

  const filterLayer = (layerId, valueRange) => {
    setMapLayers(prev => prev.map(l => {
      if (l.id !== layerId) return l;
      if (valueRange == null) {
        return { ...l, valueFilter: null };
      }
      // If clicking the same range again, clear the filter
      const currentFilter = l.valueFilter;
      if (currentFilter && currentFilter.min === valueRange.min && currentFilter.max === valueRange.max) {
        return { ...l, valueFilter: null };
      }
      return { ...l, valueFilter: valueRange };
    }));
  };

  const togglePanel = useCallback((panel) => {
    setShowPanels(prev => {
      const next = { ...prev, [panel]: !prev[panel] };
      if (!Object.values(next).some(Boolean)) return prev;
      return next;
    });
  }, []);

  const handleStartTour = useCallback(() => {
    // Save current panel state so we can restore it after the tour
    preTourPanelsRef.current = { ...showPanels };
    // Ensure all panels are visible so every tour step has a target
    setShowPanels({ chat: true, map: true, data: true });
    setIsTourRunning(true);
  }, [showPanels]);

  const handleFinishTour = useCallback(() => {
    setIsTourRunning(false);
    // Restore panels to their state before the tour started
    if (preTourPanelsRef.current) {
      setShowPanels(preTourPanelsRef.current);
      preTourPanelsRef.current = null;
    }
  }, []);

  const handleRemoveLayer = (layerId) => {
    setMapLayers(prev => prev.filter(l => l.id !== layerId));
  };

  const handleFlyToLayer = (layerId) => {
    const layer = mapLayers.find(l => l.id === layerId);
    if (!layer || !layer.data || !layer.data.features || layer.data.features.length === 0) {
      return;
    }

    // Use filtered data if filter is active, otherwise use full data
    let dataToUse = layer.data;
    if (layer.valueFilter && layer.data && layer.data.features) {
      dataToUse = {
        ...layer.data,
        features: layer.data.features.filter(feature => {
          const colorKey = layer.config?.colorProperty || 'value';
          const value = feature.properties?.[colorKey];
          if (value === null || value === undefined) return false;
          // For non-last bands, use exclusive upper bound (<) to avoid overlaps
          // For last band, use inclusive upper bound (<=)
          if (layer.valueFilter.isLast) {
            return value >= layer.valueFilter.min && value <= layer.valueFilter.max;
          } else {
            return value >= layer.valueFilter.min && value < layer.valueFilter.max;
          }
        })
      };
    }

    const bounds = getGeoJSONFitBounds(dataToUse);
    if (bounds && mapRef.current) {
      mapRef.current.fitBounds(bounds, { padding: 100, duration: 1500 });
    }
  };

  const handleMapQuery = async (layerIdOrSpec, name, config = {}) => {
    const { assistantMessageId, sql, stableId } = config;

    // Determine if first parameter is a layerId (UUID string) or a query spec (object)
    const isLayerId = typeof layerIdOrSpec === 'string' && layerIdOrSpec.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    const layerId = isLayerId ? layerIdOrSpec : null;
    const querySpec = isLayerId ? null : layerIdOrSpec;

    const newId = stableId || layerId || `map-${Date.now()}`;

    // Immediately add layer with loading state
    const loadingLayer = {
      id: newId,
      name: name || 'AI-Map Layer',
      visible: true,
      hue: Math.floor(Math.random() * 360),
      sat: 72,
      dataset: null,  // Will be loaded from database or spec
      data: null,
      stats: null,
      palette: config.palette || getDefaultPalette(),
      config: config,
      layer_id: layerId,  // Store layer_id for reference (null if using spec)
      status: 'loading',
      progress: 'Fetching data...',
      progressPercent: 0
    };

    setMapLayers(prev => {
      // If updating an existing layer (like preview), replace it
      if (prev.some(l => l.id === newId)) {
        return prev.map(l => l.id === newId ? loadingLayer : l);
      }
      return [loadingLayer, ...prev];
    });
    setShowPanels(prev => ({ ...prev, map: true }));

    try {
      // Update progress - Fetching data (0-30%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progress: 'Fetching data...', progressPercent: 10 } : l
      ));

      await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for smooth animation

      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progressPercent: 30 } : l
      ));

      // Query with layer_id if available, otherwise use query spec
      const queryPayload = layerId
        ? { layer_id: layerId }
        : querySpec;
      const data = await mapApi.queryMapData(queryPayload);

      // Update progress - Processing spatial data (30-70%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progress: 'Processing spatial data...', progressPercent: 50 } : l
      ));

      await new Promise(resolve => setTimeout(resolve, 100));

      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progressPercent: 70 } : l
      ));

      // Update progress - Rendering map (70-90%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progress: 'Rendering map...', progressPercent: 85 } : l
      ));

      // Detect geometry type and use appropriate conversion
      let geojson;
      if (data.items.length === 0) {
        // Handle empty results gracefully
        geojson = { type: 'FeatureCollection', features: [] };
      } else if (data.items[0].geometry) {
        geojson = itemsToGeoJSON(data.items);
      } else if (data.items[0].h3) {
        geojson = h3ToGeoJSON(data.items);
      } else {
        throw new Error('Unknown geometry type in map data');
      }

      // Update progress - Complete (100%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progressPercent: 100 } : l
      ));

      await new Promise(resolve => setTimeout(resolve, 200)); // Brief delay to show 100%

      const resolvedQuery = data.query_spec || queryPayload;
      const savedStyle = extractPersistableLayerConfig({
        ...(data.layer_config || {}),
        ...config,
      });
      const colorProperty = savedStyle.colorProperty ?? 'value';
      const layerForCard = {
        data: geojson,
        stats: data.stats,
        config: {
          colorProperty,
          uniformColor: savedStyle.uniformColor ?? false,
        },
      };
      const computedHighCardinality = isHighCardinalityCategorical(layerForCard, colorProperty);
      const uniformColor = savedStyle.uniformColor ?? computedHighCardinality;
      const highCardinality = savedStyle.highCardinalityCategorical ?? computedHighCardinality;
      const mergedConfig = mergeSavedLayerConfig(savedStyle, {
        palette: savedStyle.palette ?? getDefaultPalette(),
        inverted: savedStyle.inverted ?? false,
        colorProperty,
        uniformColor,
        highCardinalityCategorical: highCardinality,
      });
      const palette = mergedConfig.palette ?? getDefaultPalette();

      const newLayer = {
        id: newId,
        name: name || 'AI-Map Layer',
        visible: true,
        hue: Math.floor(Math.random() * 360),
        sat: 72,
        dataset: resolvedQuery?.dataset || null,
        data: geojson,
        stats: data.stats,
        palette,
        config: mergedConfig,
        layer_id: layerId,  // Store layer_id for reference
        query: resolvedQuery,
        status: 'loaded',
        progress: null,
        progressPercent: null
      };

      setMapLayers(prev => {
        return prev.map(l => l.id === newId ? newLayer : l);
      });

      // Update chat message if assistantMessageId provided
      if (assistantMessageId) {
        setMessages(prev => {
          const newMessages = [...prev];
          const msgIdx = newMessages.findLastIndex(m => m.id === assistantMessageId);
          if (msgIdx !== -1) {
            const msg = { ...newMessages[msgIdx] };
            msg.sql = sql;
            msg.chartData = data.items.slice(0, 100); // Sample data for display
            msg.rowCount = data.items.length;
            msg.isMapResult = true;
            newMessages[msgIdx] = msg;
          }
          return newMessages;
        });
      }

      // Auto-zoom to fit the new layer data
      const bounds = getGeoJSONFitBounds(geojson);
      if (bounds && mapRef.current) {
        // Small delay to ensure map is ready
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.fitBounds(bounds, { padding: 100, duration: 1500 });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Failed to load map data:', error);
      // Update layer with error state
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? {
          ...l,
          status: 'error',
          progress: MAP_DATA_LOAD_ERROR_HINT,
          progressPercent: null,
          error: error.message || 'Unknown error'
        } : l
      ));
    }
  };

  const handlePreviewLayer = async (query, description, config, layerId = null) => {
    await handleMapQuery(layerId, description, { ...config, stableId: 'preview-layer' });
  };

  const handleViewMapLayer = useCallback(async (layerId) => {
    // Find the layer in mapLayers by layer_id
    const layer = mapLayers.find(l => l.layer_id === layerId);
    if (layer) {
      // Layer is already on the map, just ensure map panel is visible
      setShowPanels(prev => ({ ...prev, map: true }));
      // Optionally fly to the layer bounds if it has data
      if (layer.data && mapRef.current && layer.data.features?.length > 0) {
        const bounds = getGeoJSONFitBounds(layer.data);
        if (bounds) {
          mapRef.current.fitBounds(bounds, { padding: 100, duration: 1500 });
        }
      }
    } else {
      // Layer not found, try to load it from the database
      // Find the message to get the layer name
      const message = messages.find(m => m.layer_id === layerId);
      if (message) {
        await handleMapQuery(layerId, message.name || 'Map Layer', {
          stableId: `art-${layerId}`,
        });
        setShowPanels(prev => ({ ...prev, map: true }));
      } else {
        console.warn('Layer not found on map and no message found:', layerId);
      }
    }
  }, [mapLayers, messages, mapRef]);

  const handleGenerateMapLayer = useCallback(async (originalQuery) => {
    if (!originalQuery || !originalQuery.trim()) return;
    if (!chatId) return;

    // Find the message that triggered this and mark it as generating and requested
    setMessages(prev => prev.map(msg => {
      if (msg.originalQuery === originalQuery && msg.mappable) {
        return { ...msg, isGeneratingMap: true, mapLayerRequested: true };
      }
      return msg;
    }));

    const datasetIds = workspace.sources.map(s => s.id);
    const assistantMessageId = Date.now();

    // Add a new assistant message for the map generation
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: 'Generating map layer...',
      isLoading: true,
      agentSteps: [],
      isMapResult: true // Mark as map result early to prevent UI flash
    }]);

    try {
      await chatApi.generateMapLayer({
        chatId,
        text: originalQuery,
        datasets: datasetIds,
        model: 'openai',
        onEvent: (event) => {
        // Handle side effects outside of the functional state update
        if (event.type === 'map_query') {
          handleMapQuery(event.layer_id, event.name, {
            assistantMessageId,
            sql: event.sql,
            stableId: `art-${event.layer_id}`,
          });
        }
        if (event.type === 'error' && isNoDatasetsChatError(event.content)) {
          promptAddDataset();
        }

        setMessages(prev => {
          const newMessages = [...prev];
          const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
          if (lastMsgIndex === -1) return prev;

          const lastMsg = { ...newMessages[lastMsgIndex] };

          if (event.type === 'summary') {
            lastMsg.content = (lastMsg.content ? lastMsg.content + '\n\n' : '') + event.content;
          } else if (event.type === 'agent_step') {
            const currentSteps = lastMsg.agentSteps || [];
            const stepExists = currentSteps.some(
              s => s.step === event.step && s.timestamp === (event.ts || Date.now())
            );
            if (!stepExists) {
              lastMsg.agentSteps = [
                ...currentSteps,
                {
                  step: event.step,
                  agent: event.agent,
                  timestamp: event.ts || Date.now()
                }
              ];
            }
            if (event.agent === 'insight_agent') {
              lastMsg.insightLoading = true;
            }
          } else if (event.type === 'map_query') {
            lastMsg.content = event.description || 'Map layer generated';
            lastMsg.layer_id = event.layer_id;
            lastMsg.name = event.name;
            lastMsg.sql = event.sql;
            lastMsg.isMapResult = true;
            lastMsg.isLoading = false;
          } else if (event.type === 'error') {
            lastMsg.content = (lastMsg.content || '') + '\n' + (event.content || 'An error occurred.');
            lastMsg.isLoading = false;
            lastMsg.insightLoading = false;
            if (isNoDatasetsChatError(event.content)) {
              lastMsg.linkDatasetHint = true;
            }
          } else if (event.type === 'insight') {
            lastMsg.insightLoading = false;
            lastMsg.insight = {
              key_findings: event.key_findings || [],
            };
          }

          newMessages[lastMsgIndex] = lastMsg;
          return newMessages;
        });
        },
        modelTier: chatModelTier
      });

      // Ensure loading is set to false when stream completes
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            isLoading: false,
            insightLoading: false,
          };
        }
        // Also clear isGeneratingMap from the original message
        return newMessages.map(msg => {
          if (msg.originalQuery === originalQuery && msg.mappable) {
            return { ...msg, isGeneratingMap: false };
          }
          return msg;
        });
      });
    } catch (error) {
      console.error('Map generation error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            content: 'Sorry, I encountered an error generating the map layer. Please try again.',
            isLoading: false
          };
        }
        // Clear isGeneratingMap from the original message
        return newMessages.map(msg => {
          if (msg.originalQuery === originalQuery && msg.mappable) {
            return { ...msg, isGeneratingMap: false };
          }
          return msg;
        });
      });
    }
  }, [chatId, workspace?.sources, chatModelTier, promptAddDataset]);

  const handleSend = useCallback(async (text) => {
    const messageText = text;
    if (!messageText || !messageText.trim()) return;

    // Prevent sending if chat is already loading
    if (isChatLoading) return;

    setMessages(prev => [...prev, { role: 'user', content: messageText }]);

    // Add placeholder assistant message
    const assistantMessageId = Date.now();
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      isLoading: true
    }]);

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsChatLoading(true);

    try {
      // Ensure the new chat is associated with the current workspace ID
      const isNewChat = !chatId;
      const currentChatId = chatId || (await chatApi.createChat(id)).id;
      if (isNewChat) {
        setChatId(currentChatId);
        setChatTitle('Untitled Chat');
        persistChatId(currentChatId); // Persist new chat to URL
      }

      const datasetIds = workspace.sources.map(s => s.id);

      await chatApi.streamChat({
        chatId: currentChatId,
        text: messageText,
        datasets: datasetIds,
        model: 'openai',
        onEvent: (event) => {
        // Handle side effects outside of the functional state update
        if (event.type === 'map_query') {
          handleMapQuery(event.layer_id, event.name, {
            assistantMessageId,
            sql: event.sql,
            stableId: `art-${event.layer_id}`,
          });
        }
        if (event.type === 'error' && isNoDatasetsChatError(event.content)) {
          promptAddDataset();
        }

        setMessages(prev => {
          const newMessages = [...prev];
          const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
          if (lastMsgIndex === -1) return prev;

          const lastMsg = { ...newMessages[lastMsgIndex] };

          if (event.type === 'summary') {
            lastMsg.content = (lastMsg.content ? lastMsg.content + '\n\n' : '') + event.content;
            // Keep loading until we get a result
          } else if (event.type === 'sql') {
            lastMsg.sql = event.content;
            // Store mappable flag if present
            if (event.mappable !== undefined) {
              lastMsg.mappable = event.mappable;
            }
            // Early detection of map result to prevent UI flash
            if (event.is_map_result) {
              lastMsg.isMapResult = true;
            }
            // Store original query for map generation
            if (!lastMsg.originalQuery) {
              lastMsg.originalQuery = messageText;
            }
            // Keep loading until we get a result
          } else if (event.type === 'agent_step') {
            // Initialize agentSteps array if it doesn't exist
            const currentSteps = lastMsg.agentSteps || [];
            // Check if this step already exists (by step text and timestamp)
            const stepExists = currentSteps.some(
              s => s.step === event.step && s.timestamp === (event.ts || Date.now())
            );
            // Only add if it doesn't exist
            if (!stepExists) {
              lastMsg.agentSteps = [
                ...currentSteps,
                {
                  step: event.step,
                  agent: event.agent,
                  timestamp: event.ts || Date.now()
                }
              ];
            }
            if (event.agent === 'insight_agent') {
              lastMsg.insightLoading = true;
            }
            const rowCountMatch = event.step?.match(/returned\s+(\d+)\s+rows?/i);
            if (rowCountMatch) {
              lastMsg.rowCount = parseInt(rowCountMatch[1], 10);
            }
            // Keep loading while agent is working
          } else if (event.type === 'map_query') {
            lastMsg.content = event.description || 'Map layer generated';
            lastMsg.layer_id = event.layer_id;
            lastMsg.name = event.name;
            lastMsg.sql = event.sql;
            lastMsg.isMapResult = true;
            lastMsg.isLoading = false; // Map query is a final result
            // Keep agent steps, just minimize them
          } else if (event.type === 'result') {
            // Updated to use the real structure from the backend
            if (event.visualizationSpec) {
              lastMsg.chartData = event.rows;
              lastMsg.spec = event.visualizationSpec;
              lastMsg.tableOnly = false;
              lastMsg.visualization = event.visualizationSpec.title || 'Dynamic Result';
              // Set row count from actual data if not already set
              if (lastMsg.rowCount === undefined && event.rows) {
                lastMsg.rowCount = event.rows.length;
              }
            } else if ((event.presentation === 'table' || event.table_only) && event.rows) {
              lastMsg.chartData = event.rows;
              lastMsg.tableOnly = true;
              lastMsg.spec = undefined;
              lastMsg.visualization = undefined;
              if (lastMsg.rowCount === undefined) {
                lastMsg.rowCount = event.rows.length;
              }
            } else if (event.content) {
              lastMsg.content = (lastMsg.content || '') + '\n' + event.content;
            }

            lastMsg.isLoading = false; // Result means we're done
            // Keep agent steps, just minimize them
          } else if (event.type === 'error') {
            lastMsg.content = (lastMsg.content || '') + '\n' + (event.content || 'An error occurred.');
            lastMsg.isLoading = false; // Error means we're done
            lastMsg.insightLoading = false;
            if (isNoDatasetsChatError(event.content)) {
              lastMsg.linkDatasetHint = true;
            }
            // Keep agent steps, just minimize them
          } else if (event.type === 'more_questions') {
            lastMsg.suggestions = event.questions || [];
          } else if (event.type === 'insight') {
            lastMsg.insightLoading = false;
            lastMsg.insight = {
              key_findings: event.key_findings || [],
            };
          }

          newMessages[lastMsgIndex] = lastMsg;
          return newMessages;
        });
        },
        abortSignal: abortController.signal,
        modelTier: chatModelTier
      });

      // Ensure loading is set to false when stream completes
      // Agent steps are kept and will be minimized
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            isLoading: false,
            insightLoading: false,
          };
        }
        return newMessages;
      });

      // After first message in a new chat, auto-generate title
      if (isNewChat || (messages.length === 1 && messages[0].role === 'assistant')) {
        handleGenerateTitle(currentChatId);
      }
    } catch (error) {
      // Don't show error if it was aborted
      if (error.name === 'AbortError') {
        // Stream was stopped, don't show error
        return;
      }
      console.error('Chat error:', error);
      const message =
        error?.detail ||
        (error?.code === 'quota_exhausted'
          ? "You've reached your message limit for this plan. Upgrade your plan or try again when your limit resets."
          : 'Sorry, I encountered an error. Please try again.');
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            content: message,
            isLoading: false
          };
        }
        return newMessages;
      });
    } finally {
      setIsChatLoading(false);
      abortControllerRef.current = null;
    }
  }, [
    id,
    chatId,
    workspace?.sources,
    isChatLoading,
    messages,
    handleGenerateTitle,
    persistChatId,
    chatModelTier,
    promptAddDataset
  ]);

  const handleStopChat = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsChatLoading(false);

    // Add stop message
    setMessages(prev => {
      const newMessages = [...prev];
      // Find the last assistant message that's loading
      const lastLoadingIndex = newMessages.findLastIndex(m => m.role === 'assistant' && m.isLoading);
      if (lastLoadingIndex !== -1) {
        // Mark it as not loading
        newMessages[lastLoadingIndex] = {
          ...newMessages[lastLoadingIndex],
          isLoading: false
        };
      }

      // Add stop message
      return [...newMessages, {
        id: Date.now(),
        role: 'assistant',
        content: '*Chat stopped by user. Even though the chat was stopped, the results may still be loading and will be available in the chat history.*',
        isLoading: false
      }];
    });
  }, []);

  const handleScrollComplete = useCallback(() => setTargetMessageId(null), []);

  const handleRemoveSource = (sourceId) => {
    if (removingSourceId) return;
    const source = workspace?.sources?.find((s) => s.id === sourceId);
    if (source?.status === 'loading' || source?.status === 'generating') {
      return;
    }
    setTargetSourceToRemove(sourceId);
    setIsRemoveSourceModalOpen(true);
  };

  const confirmRemoveSource = async () => {
    if (!targetSourceToRemove) return;
    const sourceId = targetSourceToRemove;
    setRemovingSourceId(sourceId);
    setIsRemoveSourceModalOpen(false);
    try {
      const updatedSources = workspace.sources.filter(s => s.id !== sourceId);
      const updatedWs = await workspaceApi.updateWorkspace(id, {
        name: workspace.name,
        description: workspace.description,
        sources: updatedSources.map(s => s.id)
      });
      setWorkspace(updatedWs);
      if (previewDataset?.id === sourceId) {
        setPreviewDataset(null);
      }
      showSuccessToast('Dataset removed from workspace.');
    } catch (err) {
      console.error('Failed to remove source:', err);
      showErrorToast(err?.response?.data?.detail || 'Failed to remove dataset from workspace.');
    } finally {
      setTargetSourceToRemove(null);
      setRemovingSourceId(null);
    }
  };

  const confirmRemoveFunction = async () => {
    if (!targetFunctionToRemove) return;
    try {
      const updatedUdfs = workspace.udfs.filter(u => u.id !== targetFunctionToRemove);
      const updatedWs = await workspaceApi.updateWorkspace(id, {
        name: workspace.name,
        description: workspace.description,
        udf_ids: updatedUdfs.map(u => u.id)
      });
      setWorkspace(updatedWs);
      if (previewFunction?.id === targetFunctionToRemove) {
        setPreviewFunction(null);
      }
      showSuccessToast('Function removed from workspace.');
    } catch (err) {
      console.error('Failed to remove function:', err);
    } finally {
      setIsRemoveFunctionModalOpen(false);
      setTargetFunctionToRemove(null);
    }
  };

  const handleConnect = async (method, data) => {
    try {
      if (method === 'bigquery') {
        // 1. Import tables into TableMetadata first
        await datasetApi.importBigQueryTables(data, null, { suppressGlobalErrorToast: true });

        // 2. Now associate them with the workspace
        const newTableIds = data.tables.map(tbl => tbl.id);
        const currentTableIds = workspace.sources.map(s => s.id);

        // Remove duplicates
        const combinedTableIds = Array.from(new Set([...newTableIds, ...currentTableIds]));
        if (combinedTableIds.length > MAX_DATASETS_PER_WORKSPACE) {
          showErrorToast(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
          return;
        }

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          sources: combinedTableIds
        }, { suppressGlobalErrorToast: true });

        setWorkspace(updatedWs);
        showInfoToast('Tables imported and linked to this workspace.');
      } else if (method === 'upload') {
        const tableId = data.table_id;
        const currentTableIds = workspace.sources.map(s => s.id);
        const combinedTableIds = Array.from(new Set([tableId, ...currentTableIds]));
        if (combinedTableIds.length > MAX_DATASETS_PER_WORKSPACE) {
          showErrorToast(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
          return;
        }

        const optimisticSource = buildOptimisticUploadDataset(tableId);
        setWorkspace((prev) => ({
          ...prev,
          sources: [optimisticSource, ...prev.sources.filter((s) => s.id !== tableId)],
        }));

        workspaceApi
          .updateWorkspace(
            id,
            {
              name: workspace.name,
              description: workspace.description,
              sources: combinedTableIds,
            },
            { suppressGlobalErrorToast: true }
          )
          .then((updatedWs) => {
            setWorkspace(updatedWs);
            showSuccessToast('Dataset added to workspace.');
          })
          .catch((err) => {
            console.error('Failed to link upload to workspace:', err);
            setWorkspace((prev) => ({
              ...prev,
              sources: prev.sources.filter((s) => s.id !== tableId),
            }));
            showErrorToast(
              err?.response?.data?.detail || 'Failed to link dataset to workspace.'
            );
          });
      } else if (method === 'library') {
        // Handle both single dataset and array of datasets
        const datasetsToAdd = Array.isArray(data) ? data : [data];
        const newTableIds = datasetsToAdd.map(d => d.id);
        const currentTableIds = workspace.sources.map(s => s.id);
        const combinedTableIds = Array.from(new Set([...newTableIds, ...currentTableIds]));
        if (combinedTableIds.length > MAX_DATASETS_PER_WORKSPACE) {
          showErrorToast(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
          return;
        }

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          sources: combinedTableIds
        }, { suppressGlobalErrorToast: true });

        setWorkspace(updatedWs);
        const count = datasetsToAdd.length;
        showSuccessToast(count === 1 ? 'Dataset added to workspace.' : count + ' datasets added to workspace.');
      } else if (method === 'sample') {
        // Sample was already added to user's library by ConnectModal (datasetApi.addSample)
        // Now associate it with this workspace
        const currentTableIds = workspace.sources.map(s => s.id);
        const combinedTableIds = Array.from(new Set([data.id, ...currentTableIds]));
        if (combinedTableIds.length > MAX_DATASETS_PER_WORKSPACE) {
          showErrorToast(`A workspace can contain at most ${MAX_DATASETS_PER_WORKSPACE} datasets.`);
          return;
        }

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          sources: combinedTableIds
        }, { suppressGlobalErrorToast: true });

        setWorkspace(updatedWs);
      } else if (method === 'udf-library') {
        // Handle both single UDF and array of UDFs
        const udfsToAdd = Array.isArray(data) ? data : [data];
        const newUdfIds = udfsToAdd.map(u => u.id);
        const currentUdfIds = (workspace.udfs || []).map(u => u.id);
        const combinedUdfIds = Array.from(new Set([...newUdfIds, ...currentUdfIds]));
        if (combinedUdfIds.length > MAX_FUNCTIONS_PER_WORKSPACE) {
          showErrorToast(`A workspace can contain at most ${MAX_FUNCTIONS_PER_WORKSPACE} functions.`);
          return;
        }

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          udf_ids: combinedUdfIds
        }, { suppressGlobalErrorToast: true });

        setWorkspace(updatedWs);
        // Auto-select the first newly added function if only one was added
        if (udfsToAdd.length === 1) {
          const addedUdf = updatedWs.udfs?.find(u => u.id === udfsToAdd[0].id);
          if (addedUdf) {
            setPreviewFunction(addedUdf);
            setPreviewDataset(null);
          }
        }
        showSuccessToast('Function library updated.');
      }
    } catch (err) {
      console.error('Failed to connect dataset via ' + method + ':', err);
      showErrorToast(err?.response?.data?.detail || 'Failed to update workspace.');
    }
  };


  const handleRebuildSource = async (sourceId) => {
    try {
      await datasetApi.rebuildMetadata(sourceId);
      // Refresh workspace data to show processing status
      const data = await workspaceApi.getWorkspaceById(id);
      setWorkspace(data);
      showInfoToast('Metadata refresh started.');
    } catch (err) {
      console.error('Failed to rebuild metadata:', err);
    }
  };

  const chatWorkspaceHeader = workspace ? (
    <div className="mb-4 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-white via-white to-emerald-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-emerald-950/20 p-4 shadow-sm backdrop-blur-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[9px] font-black text-emerald-600/80 dark:text-emerald-400/80 uppercase tracking-[0.18em] mb-1">Workspace</p>
          <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight truncate">
            {workspace?.name || 'Untitled Workspace'}
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 leading-relaxed line-clamp-2">
            {workspace?.description || 'No description provided.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleOpenSummary('overview')}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-emerald-50/60 dark:hover:bg-emerald-900/20 transition-all"
        >
          <Pencil size={12} />
          Edit
        </button>
      </div>
    </div>
  ) : null;

  const onHover = (event) => {
    const hoveredFeature = event.features?.[0];

    if (hoveredFeature) {
      setHoverInfo({
        feature: hoveredFeature,
        lngLat: event.lngLat
      });
    } else {
      setHoverInfo(null);
    }
  };

  const onMapClick = (event) => {
    const clickedFeature = event.features?.[0];

    if (clickedFeature) {
      setSelectedInfo({
        feature: clickedFeature,
        lngLat: event.lngLat
      });
    } else {
      setSelectedInfo(null);
    }
  };


  if (notFound) return <NotFoundPage />;
  if (!workspace) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950 overflow-hidden transition-colors duration-300">
      <HeaderUpdater
        workspace={workspace}
        workspaceId={id}
        bqStatus={bqStatus}
        showPanels={showPanels}
        togglePanel={togglePanel}
        onStartTour={handleStartTour}
        workspaceStats={workspaceStats}
        workspaceStatsLoading={isWorkspaceHistoryLoading}
        onOpenSummary={handleOpenSummary}
      />
      <GuidedTour run={isTourRunning} onFinish={handleFinishTour} />
      {isFullScreenPreviewOpen && (
        <PreviewTable
          dataset={previewDataset}
          onClose={() => setIsFullScreenPreviewOpen(false)}
          onMetadataUpdate={() => setPreviewMetadataVersion(v => v + 1)}
        />
      )}
      <FunctionDetailModal
        func={previewFunction}
        isOpen={isFunctionDetailOpen && !!previewFunction}
        onClose={() => setIsFunctionDetailOpen(false)}
        onUpdate={loadWorkspace}
      />
      <ConnectModal
        isOpen={isConnectModalOpen}
        mode="workspace"
        onClose={async () => {
          setIsConnectModalOpen(false);
          setConnectModalInitialStep(null);
          setShouldResumeImport(false);
          try {
            const bq = await bigqueryApi.getStatus();
            setBqStatus(bq);
          } catch {
            /* keep existing status */
          }
        }}
        onConnect={handleConnect}
        initialStep={connectModalInitialStep || (shouldResumeImport ? 'bigquery-dataset' : null)}
        onBillingRequired={() => {
          setIsConnectModalOpen(false);
          setShouldResumeImport(true);
          setIsBillingModalOpen(true);
        }}
        currentSources={workspace?.sources || []}
      />

      <FunctionLibraryModal
        isOpen={isFunctionLibraryModalOpen}
        onClose={() => setIsFunctionLibraryModalOpen(false)}
        onSelect={(udfs) => {
          handleConnect('udf-library', udfs);
          setIsFunctionLibraryModalOpen(false);
        }}
        currentUdfIds={workspace?.udfs?.map(u => u.id) || []}
      />
      <AddItemModal
        isOpen={isAddItemModalOpen}
        type={addItemModalType}
        onClose={() => setIsAddItemModalOpen(false)}
        onSelect={async (method, data) => {
          await handleConnect(method, data);
        }}
        currentItemIds={
          addItemModalType === 'dataset'
            ? workspace?.sources?.map(s => s.id) || []
            : workspace?.udfs?.map(u => u.id) || []
        }
      />
      <BillingProjectModal
        isOpen={isBillingModalOpen}
        onClose={() => {
          setIsBillingModalOpen(false);
          setShouldResumeImport(false);
        }}
        onProjectSelected={async () => {
          const bq = await bigqueryApi.getStatus();
          setBqStatus(bq);
          if (shouldResumeImport) {
            setShouldResumeImport(false);
            setIsConnectModalOpen(true);
          }
        }}
        currentBillingProject={bqStatus.billing_project_id}
      />
      {workspaceNeedsBqReauth &&
        createPortal(
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-gray-900/55 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bq-reauth-title"
            aria-describedby="bq-reauth-desc"
          >
            <div className="relative w-full max-w-md rounded-[32px] border border-red-100 dark:border-red-900/40 bg-white dark:bg-gray-950 shadow-2xl p-8 animate-in zoom-in-95 duration-200">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 rounded-2xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 shrink-0">
                  <AlertCircle size={28} />
                </div>
                <div className="min-w-0">
                  <h2
                    id="bq-reauth-title"
                    className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight leading-tight mb-2"
                  >
                    Reconnect required
                  </h2>
                  <p
                    id="bq-reauth-desc"
                    className="text-sm text-gray-600 dark:text-gray-400 font-medium leading-relaxed"
                  >
                    Your Google session for BigQuery expired.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  className="w-full py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 transition-all active:scale-[0.98]"
                  onClick={() => navigate('/sources?bigquery-reauth=1')}
                >
                  Reconnect
                </button>
                <button
                  type="button"
                  className="w-full py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  onClick={() => navigate('/workspaces')}
                >
                  Back to workspaces
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <WorkspaceSummaryModal
        workspace={workspace}
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        onOpenWorkspace={() => setIsSummaryModalOpen(false)}
        onUpdateWorkspace={handleUpdateWorkspaceFromModal}
        onSelectArtefact={handleSelectArtefact}
        initialTab={summaryModalTab}
        showOpenWorkspace={false}
        prefetchedHistory={workspaceHistory}
        historyLoading={isWorkspaceHistoryLoading}
      />

      <MapLayerBuilderModal
        isOpen={isMapLayerBuilderModalOpen}
        onClose={() => {
          setIsMapLayerBuilderModalOpen(false);
          // Clean up any temporary preview ghost when closing
          handleRemoveLayer('preview-layer');
        }}
        workspaceId={id}
        chatId={chatId}
        availableDatasets={workspace?.sources || []}
        onSave={(payload, desc, config, layerId) => {
          // Clean up any ghost preview before adding the permanent layer
          handleRemoveLayer('preview-layer');
          // If layerId exists, use it; otherwise use the query spec (payload)
          const stableId = layerId ? 'art-' + layerId : undefined;
          handleMapQuery(layerId || payload, desc, { ...config, stableId });
        }}
        onPreview={handlePreviewLayer}
      />

      <ConfirmModal
        isOpen={isRemoveSourceModalOpen}
        onClose={() => setIsRemoveSourceModalOpen(false)}
        onConfirm={confirmRemoveSource}
        title="Remove Dataset"
        description="Are you sure you want to remove this dataset from this workspace? This won't delete the data from the library."
        confirmText="Remove"
        variant="danger"
      />

      <ConfirmModal
        isOpen={isRemoveFunctionModalOpen}
        onClose={() => setIsRemoveFunctionModalOpen(false)}
        onConfirm={confirmRemoveFunction}
        title="Remove Function"
        description="Are you sure you want to remove this function from this workspace?"
        confirmText="Remove"
        variant="danger"
      />

      <div className="flex-1 overflow-hidden">
        <PanelGroup orientation="vertical" className="h-full w-full">
          {(showPanels.chat || showPanels.map) && (
            <Panel defaultSize={showPanels.data ? 82 : 100} minSize={25}>
              <PanelGroup orientation="horizontal" className="h-full w-full">
                {showPanels.chat && (
                  <Panel minSize={25} defaultSize={showPanels.map ? 50 : 100}>
                    <div data-tour="chat-panel" className="h-full">
                      <ChatPanel
                        messages={messages}
                        chatHeader={chatWorkspaceHeader}
                        chatTitle={chatTitle}
                        onSend={handleSend}
                        targetMessageId={targetMessageId}
                        onScrollComplete={handleScrollComplete}
                        isChatLoading={isChatLoading}
                        isInitializingChat={isInitializingChat}
                        focusInputKey={id}
                        onStopChat={handleStopChat}
                        newChatTo={newChatTo}
                        onAddLayer={handleGenerateMapLayer}
                        onViewMapLayer={handleViewMapLayer}
                        onLinkDataset={handleAddDataset}
                        modelTier={chatModelTier}
                        onModelTierChange={setChatModelTier}
                      />
                    </div>
                  </Panel>
                )}
                {showPanels.chat && showPanels.map && (
                  <Separator orientation="horizontal" />
                )}
                {showPanels.map && (
                  <Panel minSize={25} defaultSize={showPanels.chat ? 50 : 100}>
                    <div data-tour="map-panel" className="h-full w-full bg-white dark:bg-gray-950 relative">
                      <MapPanel
                        mapRef={mapRef}
                        mapLayers={mapLayers}
                        maxFeaturesPerLayer={maxFeaturesPerLayerMap}
                        hoverInfo={hoverInfo}
                        selectedInfo={selectedInfo}
                        onMouseMove={onHover}
                        onMapClick={onMapClick}
                        onToggleLayerVisibility={toggleLayerVisibility}
                        onRemoveLayer={handleRemoveLayer}
                        onUpdateLayerTheme={updateLayerTheme}
                        onOpenBuilder={() => setIsMapLayerBuilderModalOpen(true)}
                      />

                      <MapLayerList
                        layers={mapLayers}
                        workspaceId={id}
                        chatId={chatId}
                        maxFeaturesPerLayer={maxFeaturesPerLayerMap}
                        onMaxFeaturesPerLayerChange={setMaxFeaturesPerLayerMap}
                        availableDatasets={workspace?.sources || []}
                        onToggleVisibility={toggleLayerVisibility}
                        onRemoveLayer={handleRemoveLayer}
                        onUpdateTheme={updateLayerTheme}
                        onUpdateLayerClassification={updateLayerClassification}
                        onUpdateLayerMetric={handleUpdateLayerMetric}
                        onUpdateLayerColorProperty={updateLayerColorProperty}
                        onPersistLayerStyle={persistLayerStyle}
                        onAddLayer={handleMapQuery}
                        onPreviewLayer={handlePreviewLayer}
                        onOpenBuilder={() => setIsMapLayerBuilderModalOpen(true)}
                        onFilterLayer={filterLayer}
                        onFlyToLayer={handleFlyToLayer}
                        onReorderLayers={handleReorderLayers}
                      />
                    </div>
                  </Panel>
                )}
              </PanelGroup>
            </Panel>
          )}
          {(showPanels.chat || showPanels.map) && showPanels.data && <Separator orientation="vertical" />}
          {showPanels.data && (
            <Panel minSize={18} defaultSize={(showPanels.chat || showPanels.map) ? 18 : 100}>
              <div data-tour="data-panel" className="h-full border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 flex flex-col overflow-hidden transition-colors">
                <PanelGroup orientation="horizontal" className="h-full w-full">
                  {/* Left column: Tabbed Sidebars */}
                  <Panel defaultSize={24} minSize={16} className="border-r border-gray-100 dark:border-gray-800">
                    <DataPanelTabs
                      sources={workspace.sources || []}
                      udfs={workspace.udfs || []}
                      previewDataset={previewDataset}
                      previewFunction={previewFunction}
                      setPreviewDataset={(ds) => {
                        setPreviewDataset(ds);
                        if (ds) setPreviewFunction(null);
                      }}
                      setPreviewFunction={(fn) => {
                        setPreviewFunction(fn);
                        if (fn) setPreviewDataset(null);
                      }}
                      onConnect={handleAddDataset}
                      onRemoveSource={handleRemoveSource}
                      onRebuildSource={handleRebuildSource}
                      removingSourceId={removingSourceId}
                      onManageFunctions={() => {
                        setAddItemModalType('function');
                        setIsAddItemModalOpen(true);
                      }}
                      onRemoveFunction={(fid) => {
                        setTargetFunctionToRemove(fid);
                        setIsRemoveFunctionModalOpen(true);
                      }}
                    />
                  </Panel>

                  <Separator orientation="horizontal" />

                  {/* Right column: Unified Preview */}
                  <Panel defaultSize={76}>
                    {previewFunction ? (
                      <FunctionPreview
                        function={previewFunction}
                        onOpenDetails={() => setIsFunctionDetailOpen(true)}
                      />
                    ) : (
                      <DatasetPreview
                        key={(previewDataset?.id || '') + '-' + previewMetadataVersion}
                        dataset={previewDataset}
                        onOpenDetails={() => setIsFullScreenPreviewOpen(true)}
                      />
                    )}
                  </Panel>
                </PanelGroup>
              </div>
            </Panel>
          )}
        </PanelGroup>
      </div>
    </div>
  );
};

const Separator = ({ orientation }) => (
  <PanelResizeHandle className={cn(
    orientation === 'horizontal' ? "w-1" : "h-1",
    "bg-gray-100 dark:bg-gray-800 hover:bg-emerald-500 transition-colors relative group shrink-0 outline-none"
  )}>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-400 group-hover:text-emerald-600 transition-colors shadow-sm z-10">
      {orientation === 'horizontal' ? <GripVertical size={12} /> : <GripHorizontal size={12} />}
    </div>
  </PanelResizeHandle>
);

export default WorkspacePage;
